import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

const CURRENCIES = [
  { value: 'TRY', label: '₺ TRY', symbol: '₺' },
  { value: 'USD', label: '$ USD', symbol: '$' },
  { value: 'EUR', label: '€ EUR', symbol: '€' },
  { value: 'GBP', label: '£ GBP', symbol: '£' },
];

interface CurrencyConversionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  invoiceDate: Date;
  currencies: string[];
  onExport: (targetCurrency: string, rates: Record<string, number>) => void;
}

export function CurrencyConversionModal({
  open,
  onOpenChange,
  invoiceId: _invoiceId,
  invoiceDate,
  currencies,
  onExport,
}: CurrencyConversionModalProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const [targetCurrency, setTargetCurrency] = React.useState('TRY');
  const [rateSource, setRateSource] = React.useState<'current' | 'invoice_date' | 'manual'>('current');
  const [manualRates, setManualRates] = React.useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = React.useState(false);

  // Get other currencies (exclude target)
  const otherCurrencies = currencies.filter(c => c !== targetCurrency);

  // For now, we'll use placeholder rates until the exchange rate queries are added
  const applicableRates = React.useMemo(() => {
    if (rateSource === 'manual') return manualRates;
    // Placeholder rates - will be replaced with actual API calls
    const defaultRates: Record<string, number> = {
      USD: 34.5,
      EUR: 37.2,
      GBP: 43.5,
      TRY: 1,
    };
    return defaultRates;
  }, [rateSource, manualRates]);

  const handleExport = () => {
    setIsLoading(true);
    onExport(targetCurrency, applicableRates);
    setIsLoading(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('invoices:export.title')}</DialogTitle>
          <DialogDescription>
            {t('invoices:export.multiCurrencyNotice')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Target Currency */}
          <div className="space-y-2">
            <Label>{t('invoices:export.targetCurrency')}</Label>
            <Select value={targetCurrency} onValueChange={setTargetCurrency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((curr) => (
                  <SelectItem key={curr.value} value={curr.value}>
                    {curr.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rate Source */}
          <div className="space-y-3">
            <Label>{t('invoices:export.rateSource')}</Label>
            <RadioGroup value={rateSource} onValueChange={(v: string) => setRateSource(v as typeof rateSource)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="current" id="current" />
                <Label htmlFor="current" className="font-normal cursor-pointer">
                  {t('invoices:export.currentRates')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="invoice_date" id="invoice_date" />
                <Label htmlFor="invoice_date" className="font-normal cursor-pointer">
                  {t('invoices:export.invoiceDateRates', { date: format(invoiceDate, 'dd MMM yyyy') })}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="manual" id="manual" />
                <Label htmlFor="manual" className="font-normal cursor-pointer">
                  {t('invoices:export.manualRates')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Rates Display/Edit */}
          {otherCurrencies.length > 0 && (
            <div className="space-y-3">
              <Label>{t('invoices:export.rates')}</Label>
              <div className="rounded-md border p-3 space-y-2">
                {otherCurrencies.map((currency) => (
                  <div key={currency} className="flex items-center justify-between gap-4">
                    <span className="text-sm">{currency} → {targetCurrency}</span>
                    {rateSource === 'manual' ? (
                      <Input
                        type="number"
                        step="0.0001"
                        className="w-32 h-8 text-right"
                        value={manualRates[currency] || ''}
                        onChange={(e) => setManualRates(prev => ({
                          ...prev,
                          [currency]: parseFloat(e.target.value) || 0
                        }))}
                        placeholder="0.0000"
                      />
                    ) : (
                      <span className="text-sm font-medium tabular-nums">
                        {(applicableRates[currency] || 1).toFixed(4)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleExport} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('invoices:export.downloadPdf')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
